import {
  AnimatedContentLoader,
  Button,
  Input,
  ModalV2,
  RadioGroup,
  TextTooltip
} from '@root/components'
import { useBusinessContext, useFlag, useFlagsLoaded } from '@root/context'
import {
  IBusiness,
  IInvoiceNumber,
  InvoicingDefaultSettlementDate,
  fetchBusiness,
  fetchInvoiceNumber,
  updateBusiness,
  updateInvoiceNumber
} from '@root/query'
import { applyHttpFormErrors, invoiceFormCacheKey } from '@root/utils'
import React, { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaHashtag, FaQuestionCircle, FaSave } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { useResetCacheFormValues } from '@root/hooks'
import { SettingsBooleanValue } from '@pages/SettingsPage/components/SettingsBooleanValue'
import { SettingsReadOnlyField } from '@pages/SettingsPage/components/SettingsReadOnlyField'
import { SettingsSubPageLayout } from '@pages/SettingsPage/components/SettingsSubPageLayout'
import { SettingsDataCard } from '@pages/SettingsPage/components/SettingsDataCard'
import { SettingsSwitchField } from '@pages/SettingsPage/components/SettingsSwitchField'

type FormType = IInvoiceNumber & Partial<IBusiness>

const settlementDateRuleLabel = (
  rule: InvoicingDefaultSettlementDate | undefined,
  labelFor: (key: string) => string
) =>
  rule === 'invoice_date'
    ? labelFor('settings.invoicing.defaultAccrualDateInvoiceDate')
    : labelFor('settings.invoicing.defaultAccrualDatePreviousMonthEnd')

const InvoiceSettingsAdvanced: React.FC = () => {
  const queryClient = useQueryClient()
  const [t] = useTranslation()
  const { slug } = useBusinessContext()
  const { resetCache: resetInvoiceFormCache } = useResetCacheFormValues(invoiceFormCacheKey(slug))
  const [isModalVisible, setIsModalVisible] = useState(false)
  const flagsLoaded = useFlagsLoaded()
  const workflowsFlag = useFlag('workflows')
  const workflowsHubEnabled = flagsLoaded && workflowsFlag.enabled

  const { data: invoiceNumberData, isLoading: isInvoiceDataLoading } = useQuery({
    queryKey: [slug, 'invoice_number'],
    queryFn: () => fetchInvoiceNumber({ slug })
  })

  const { data: businessData, isLoading: isBusinessDataLoading } = useQuery({
    queryKey: [slug],
    queryFn: () => fetchBusiness({ slug })
  })

  const defaultValues = useMemo<FormType>(
    () => ({
      next_invoice_number: invoiceNumberData?.next_invoice_number,
      invoicing_default_penalty_interest: businessData?.invoicing_default_penalty_interest,
      invoicing_default_payment_condition_days:
        businessData?.invoicing_default_payment_condition_days,
      invoicing_default_settlement_date:
        businessData?.invoicing_default_settlement_date ?? 'previous_month_end',
      ...(workflowsHubEnabled
        ? {}
        : {
            has_accrual_based_entries_for_invoicing:
              businessData?.has_accrual_based_entries_for_invoicing
          })
    }),
    [
      invoiceNumberData?.next_invoice_number,
      businessData?.invoicing_default_penalty_interest,
      businessData?.invoicing_default_payment_condition_days,
      businessData?.invoicing_default_settlement_date,
      businessData?.has_accrual_based_entries_for_invoicing,
      workflowsHubEnabled
    ]
  )

  const {
    register,
    handleSubmit,
    setError,
    reset,
    control,
    formState: { errors, isDirty }
  } = useForm<FormType>({
    defaultValues
  })

  const accrualEntriesSetting = useWatch({
    control,
    name: 'has_accrual_based_entries_for_invoicing'
  })
  const accrualEntriesOn = workflowsHubEnabled
    ? Boolean(businessData?.has_accrual_based_entries_for_invoicing)
    : Boolean(accrualEntriesSetting)

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const updateDataMutation = useMutation<any, unknown, FormType>({
    mutationFn: async values => {
      await Promise.all([
        updateInvoiceNumber({ slug }, { next_invoice_number: values?.next_invoice_number }),
        updateBusiness(
          { slug },
          {
            invoicing_default_penalty_interest: values?.invoicing_default_penalty_interest,
            invoicing_default_payment_condition_days:
              values?.invoicing_default_payment_condition_days,
            invoicing_default_settlement_date: values?.invoicing_default_settlement_date,
            ...(workflowsHubEnabled
              ? {}
              : {
                  has_accrual_based_entries_for_invoicing:
                    values?.has_accrual_based_entries_for_invoicing
                })
          }
        )
      ])
    },
    ...{
      onSuccess: async () => {
        await resetInvoiceFormCache()
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'invoice_number'] })
        ])
        toast.success(t('general.changesSaved'))
        setIsModalVisible(false)
      },
      onError: error => applyHttpFormErrors(error, setError)
    }
  })

  const isFormDataLoading = isInvoiceDataLoading || isBusinessDataLoading

  return (
    <SettingsSubPageLayout title={t('settings.sections.invoicingNumberingAndDefaults')}>
      <AnimatedContentLoader isLoading={isFormDataLoading}>
        <SettingsDataCard
          icon={<FaHashtag />}
          title={t('settings.sections.invoicingNumberingAndDefaults')}
          action={
            <Button
              isSecondary
              size="sm"
              data-test="invoice-advanced-edit-button"
              onClick={() => {
                reset(defaultValues)
                setIsModalVisible(true)
              }}
            >
              {t('general.edit')}
            </Button>
          }
        >
          {!workflowsHubEnabled && (
            <SettingsReadOnlyField
              layout="row"
              label={t('settings.invoicing.accrualBasedAccounting')}
              value={
                <SettingsBooleanValue
                  value={businessData?.has_accrual_based_entries_for_invoicing}
                />
              }
            />
          )}

          {businessData?.has_accrual_based_entries_for_invoicing && (
            <SettingsReadOnlyField
              layout="row"
              label={t('settings.invoicing.defaultAccrualDate')}
              value={settlementDateRuleLabel(businessData.invoicing_default_settlement_date, t)}
            />
          )}

          <SettingsReadOnlyField
            label={t('settings.invoicing.invoiceNumber')}
            value={invoiceNumberData?.next_invoice_number}
          />

          <SettingsReadOnlyField
            label={t('settings.invoicing.penaltyInterest')}
            value={businessData?.invoicing_default_penalty_interest}
          />

          <SettingsReadOnlyField
            label={t('settings.invoicing.paymentTerm')}
            value={businessData?.invoicing_default_payment_condition_days}
          />
        </SettingsDataCard>
      </AnimatedContentLoader>

      <ModalV2
        header={t('settings.sections.invoicingNumberingAndDefaults')}
        isVisible={isModalVisible}
        handleOnClose={() => setIsModalVisible(false)}
        width={500}
        footer={
          <ModalFooter>
            <Button type="button" onClick={() => setIsModalVisible(false)}>
              {t('general.cancel')}
            </Button>
            <Button
              type="submit"
              form="invoice-advanced-form"
              intent="success"
              icon={<FaSave />}
              disabled={!isDirty || updateDataMutation.isPending}
              showSpinner={updateDataMutation.isPending}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        }
      >
        <SettingsForm
          id="invoice-advanced-form"
          data-test="invoice-advanced-form"
          onSubmit={handleSubmit(values => updateDataMutation.mutate(values))}
        >
          {(!workflowsHubEnabled || accrualEntriesOn) && (
            <Section>
              {!workflowsHubEnabled && (
                <Controller
                  control={control}
                  name="has_accrual_based_entries_for_invoicing"
                  render={({ field: { onChange, value } }) => (
                    <SettingsSwitchField
                      id="has_accrual_based_entries_for_invoicing"
                      label={t('settings.invoicing.accrualBasedAccounting')}
                      hint={t('settings.invoicing.accrualBasedAccountingTooltip')}
                      checked={Boolean(value)}
                      onChange={onChange}
                    />
                  )}
                />
              )}

              {accrualEntriesOn && (
                <FieldGroup role="group" aria-labelledby="invoice-default-accrual-date-label">
                  <FieldLabel>
                    <span id="invoice-default-accrual-date-label">
                      {t('settings.invoicing.defaultAccrualDate')}
                    </span>
                    <TextTooltip tooltip={t('settings.invoicing.defaultAccrualDateInfo')}>
                      <HintIcon>
                        <FaQuestionCircle />
                      </HintIcon>
                    </TextTooltip>
                  </FieldLabel>
                  <Controller
                    control={control}
                    name="invoicing_default_settlement_date"
                    render={({ field: { onChange, value } }) => (
                      <RadioGroup
                        name="invoicing_default_settlement_date"
                        subtle
                        value={value || 'previous_month_end'}
                        onChange={next => onChange(next)}
                        options={[
                          {
                            value: 'invoice_date',
                            label: t('settings.invoicing.defaultAccrualDateInvoiceDate')
                          },
                          {
                            value: 'previous_month_end',
                            label: t('settings.invoicing.defaultAccrualDatePreviousMonthEnd')
                          }
                        ]}
                      />
                    )}
                  />
                </FieldGroup>
              )}
            </Section>
          )}

          <Section>
            <Input
              label={t('settings.invoicing.invoiceNumber')}
              errors={errors.next_invoice_number}
              required
              type="number"
              data-test="settings-invoincing-invoice-number"
              info={t('settings.invoicing.invoiceNumberInfo')}
              {...register('next_invoice_number', {
                required: { value: true, message: t('validation.required') },
                valueAsNumber: true
              })}
            />
            <Input
              label={t('settings.invoicing.penaltyInterest')}
              errors={errors.invoicing_default_penalty_interest}
              required
              type="number"
              step="0.1"
              info={t('settings.invoicing.penaltyInterestInfo')}
              {...register('invoicing_default_penalty_interest', {
                required: { value: true, message: t('validation.required') },
                valueAsNumber: true
              })}
            />
            <Input
              label={t('settings.invoicing.paymentTerm')}
              errors={errors.invoicing_default_payment_condition_days}
              required
              type="number"
              info={t('settings.invoicing.paymentTermInfo')}
              {...register('invoicing_default_payment_condition_days', {
                required: { value: true, message: t('validation.required') },
                valueAsNumber: true
              })}
            />
          </Section>
        </SettingsForm>
      </ModalV2>
    </SettingsSubPageLayout>
  )
}

export default InvoiceSettingsAdvanced

const SettingsForm = styled.form`
  max-width: 100%;
  min-width: 0;
  padding-inline: 4px;
  overflow-x: clip;
`

const Section = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing.md}rem;
    padding-top: ${({ theme }) => theme.spacing.md}rem;
    border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const FieldLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: ${({ theme }) => theme.fontSize.md}rem;
`

const HintIcon = styled.span`
  display: inline-flex;
  color: ${({ theme }) => theme.colors.metalGray};
  cursor: help;

  svg {
    width: ${({ theme }) => theme.fontSize.sm}rem;
    height: ${({ theme }) => theme.fontSize.sm}rem;
  }
`

const ModalFooter = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`
